/**
 * TwoFactorPanel — opt-in TOTP enrolment for the current user.
 *
 * UX flow:
 *   1. Show status (enabled / disabled / pending).
 *   2. "Enable 2FA" → calls /auth/2fa/enrol → renders QR + secret.
 *   3. User scans into Google Authenticator / 1Password / Authy and types
 *      the 6-digit code.
 *   4. /auth/2fa/confirm returns 8 recovery codes — we surface them
 *      EXACTLY ONCE inside a "Save these now" dialog (download as TXT).
 *
 * Disable flow asks for either a TOTP code OR the current password.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, Loader2, Copy, Download, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TwoFactorPanel({ isPt = true }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [enrol, setEnrol] = useState(null);   // {secret, qr_png_data_uri}
  const [code, setCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [showDisable, setShowDisable] = useState(false);
  const [disablePw, setDisablePw] = useState("");
  const [disableCode, setDisableCode] = useState("");

  const reload = async () => {
    try {
      const { data } = await axios.get(`${API}/auth/2fa/status`);
      setStatus(data);
    } catch { /* ignore */ }
  };
  useEffect(() => { reload(); }, []);

  const startEnrol = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/auth/2fa/enrol`);
      setEnrol(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally { setBusy(false); }
  };

  const confirmEnrol = async () => {
    if (!/^\d{6}$/.test(code)) {
      toast.error(isPt ? "Digite os 6 dígitos." : "Enter all 6 digits.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/auth/2fa/confirm`, { code });
      setRecoveryCodes(data.recovery_codes || []);
      setEnrol(null);
      setCode("");
      await reload();
      toast.success(isPt ? "2FA ativado" : "2FA enabled");
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally { setBusy(false); }
  };

  const disable = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/auth/2fa/disable`, {
        password: disablePw || null,
        code: disableCode || null,
      });
      setShowDisable(false); setDisablePw(""); setDisableCode("");
      await reload();
      toast.success(isPt ? "2FA desativado" : "2FA disabled");
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally { setBusy(false); }
  };

  const downloadCodes = () => {
    const blob = new Blob([recoveryCodes.join("\n")], { type: "text/plain" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "agentelead-recovery-codes.txt";
    a.click();
  };
  const copyCodes = async () => {
    try {
      await navigator.clipboard.writeText(recoveryCodes.join("\n"));
      toast.success(isPt ? "Copiado" : "Copied");
    } catch { toast.error("Clipboard blocked"); }
  };

  // ----- Render -----

  return (
    <section className="al-card p-6" data-testid="account-2fa">
      <h2 className="text-[13px] uppercase tracking-wide text-[var(--al-text-muted)] font-semibold mb-4 flex items-center gap-2">
        <ShieldCheck className="w-3.5 h-3.5" /> {isPt ? "Autenticação em dois fatores" : "Two-factor authentication"}
      </h2>

      {status?.enabled ? (
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center"><ShieldCheck className="w-5 h-5" /></div>
          <div className="flex-1">
            <div className="font-bold text-[14px]">2FA ON</div>
            <div className="text-[12px] text-[var(--al-text-muted)]">
              {isPt
                ? `${status.recovery_codes_remaining} códigos de recuperação restantes`
                : `${status.recovery_codes_remaining} recovery codes remaining`}
            </div>
          </div>
          <button type="button" onClick={() => setShowDisable(true)} disabled={busy}
            data-testid="2fa-disable-btn"
            className="text-[12px] font-bold text-rose-600 hover:underline">
            {isPt ? "Desativar" : "Disable"}
          </button>
        </div>
      ) : enrol ? (
        <div data-testid="2fa-enrol-form">
          <div className="flex items-start gap-4">
            <img src={enrol.qr_png_data_uri} alt="QR" className="w-40 h-40 rounded-lg bg-white p-1 border border-[var(--al-border)]" />
            <div className="flex-1 space-y-3">
              <p className="text-[12.5px] leading-relaxed">
                {isPt
                  ? "Escaneie o QR no Google Authenticator, 1Password ou Authy e digite o código gerado:"
                  : "Scan the QR with Google Authenticator, 1Password or Authy and enter the generated code:"}
              </p>
              <div className="text-[11px] font-mono-tech bg-[var(--al-bg)] border border-[var(--al-border)] rounded p-2 break-all">
                {enrol.secret}
              </div>
              <div className="flex items-center gap-2">
                <input type="text" inputMode="numeric" maxLength={6}
                  value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  placeholder="000000"
                  data-testid="2fa-code-input"
                  className="al-input h-10 text-[14px] font-mono-tech tracking-widest text-center w-32" />
                <button type="button" onClick={confirmEnrol} disabled={busy || code.length !== 6}
                  data-testid="2fa-confirm-btn"
                  className="al-btn-primary h-10 px-4 rounded-lg bg-[#C9A24E] hover:bg-[#b48d3e] text-[#1A0E36] font-bold text-[12.5px] inline-flex items-center gap-2 disabled:opacity-60">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  {isPt ? "Confirmar" : "Confirm"}
                </button>
                <button type="button" onClick={() => setEnrol(null)} className="text-[12px] text-[var(--al-text-muted)] hover:underline">
                  {isPt ? "Cancelar" : "Cancel"}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center"><ShieldAlert className="w-5 h-5" /></div>
          <div className="flex-1">
            <div className="font-bold text-[14px]">{isPt ? "2FA desativado" : "2FA disabled"}</div>
            <div className="text-[12px] text-[var(--al-text-muted)]">
              {isPt
                ? "Recomendado para Master e Admins. Adiciona uma camada de segurança ao login."
                : "Recommended for Master and Admins. Adds an extra security layer at login."}
            </div>
          </div>
          <button type="button" onClick={startEnrol} disabled={busy}
            data-testid="2fa-enrol-btn"
            className="al-btn-primary h-10 px-4 rounded-lg bg-[#C9A24E] hover:bg-[#b48d3e] text-[#1A0E36] font-bold text-[12.5px] inline-flex items-center gap-2 disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {isPt ? "Ativar 2FA" : "Enable 2FA"}
          </button>
        </div>
      )}

      {/* Recovery codes modal */}
      {recoveryCodes && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="2fa-recovery-modal">
          <div className="bg-[var(--al-card-bg)] rounded-xl p-6 max-w-md w-full">
            <h3 className="text-[16px] font-bold mb-1">{isPt ? "Salve estes códigos AGORA" : "Save these codes NOW"}</h3>
            <p className="text-[12px] text-[var(--al-text-muted)] mb-3">
              {isPt
                ? "Você pode usar cada código uma única vez para entrar caso perca o aplicativo. Não serão exibidos de novo."
                : "Each code logs you in once if you lose your authenticator. They won't be shown again."}
            </p>
            <div className="grid grid-cols-2 gap-2 font-mono-tech text-[12.5px] bg-[var(--al-bg)] border border-[var(--al-border)] rounded p-3 mb-3">
              {recoveryCodes.map((c, i) => <div key={i}>{c}</div>)}
            </div>
            <div className="flex gap-2">
              <button onClick={downloadCodes} className="flex-1 h-10 rounded-lg bg-[#3B82F6] hover:bg-[#2563EB] text-white text-[12.5px] font-bold inline-flex items-center justify-center gap-1.5">
                <Download className="w-4 h-4" /> {isPt ? "Baixar" : "Download"}
              </button>
              <button onClick={copyCodes} className="flex-1 h-10 rounded-lg border border-[var(--al-border)] text-[12.5px] font-bold inline-flex items-center justify-center gap-1.5">
                <Copy className="w-4 h-4" /> {isPt ? "Copiar" : "Copy"}
              </button>
              <button onClick={() => setRecoveryCodes(null)} className="h-10 px-3 rounded-lg text-[12.5px] font-bold text-[var(--al-text-muted)] hover:text-rose-600">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Disable modal */}
      {showDisable && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-[var(--al-card-bg)] rounded-xl p-6 max-w-md w-full">
            <h3 className="text-[16px] font-bold mb-3">{isPt ? "Desativar 2FA" : "Disable 2FA"}</h3>
            <p className="text-[12px] text-[var(--al-text-muted)] mb-3">
              {isPt
                ? "Forneça seu código TOTP atual OU sua senha:"
                : "Provide your current TOTP code OR your password:"}
            </p>
            <div className="space-y-2">
              <input type="text" inputMode="numeric" maxLength={6}
                value={disableCode} onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, ""))}
                placeholder={isPt ? "Código TOTP (6 dígitos)" : "TOTP code (6 digits)"}
                data-testid="2fa-disable-code"
                className="al-input h-10 w-full text-[14px] font-mono-tech tracking-widest text-center" />
              <div className="text-center text-[11px] text-[var(--al-text-muted)]">{isPt ? "ou" : "or"}</div>
              <input type="password" value={disablePw} onChange={(e) => setDisablePw(e.target.value)}
                placeholder={isPt ? "Senha atual" : "Current password"}
                data-testid="2fa-disable-password"
                className="al-input h-10 w-full text-[13px]" />
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={disable} disabled={busy || (!disableCode && !disablePw)}
                data-testid="2fa-disable-confirm"
                className="flex-1 h-10 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-[12.5px] font-bold inline-flex items-center justify-center gap-1.5 disabled:opacity-60">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {isPt ? "Desativar" : "Disable"}
              </button>
              <button onClick={() => { setShowDisable(false); setDisableCode(""); setDisablePw(""); }}
                className="px-4 h-10 rounded-lg border border-[var(--al-border)] text-[12.5px] font-bold">
                {isPt ? "Cancelar" : "Cancel"}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
